/**
 * Viewer suggestions reviewed by an editor (collaboration phase 3).
 *
 * - SuggestionEditor ({ projectId, path, isOpen, onClose, onSubmitted? }): a viewer's "Suggest a change": a SEPARATE
 *   editor model seeded with the file as last committed (never the live doc), then Submit with a message. vfs-service
 *   keeps it as a patch plus its base commit.
 * - SuggestionsList ({ projectId, onClose, focusId? }): the project's suggestions (Team ▾ › Notes › Suggestions (n));
 *   SuggestionsPanel ({ projectId, isOpen, onClose, focusId? }) the same in a dialog of its own. Each says who made it, on which file and lines, when, and that it was made with Suggest
 *   a change in Code; Open in Code opens the file at those lines with the editor's Suggestions tab on it. Editors and
 *   the owner Accept (a commit credited to the viewer) or Decline with a reason. When the file moved since and the
 *   suggestion overlaps a later change, the accept is refused and the lines are listed.
 * - FileSuggestions: the editor side panel's Suggestions tab (next to the comments' This file / Project): one file's.
 * - openSuggestionInCode / useSuggestionRequests, requestSuggestChange / useSuggestChangeRequests: the window events
 *   (`flow:open-code`, `collab:openSuggestion`, `collab:suggest`) between these lists and the open editor.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Editor from '@monaco-editor/react';
import Modal from '../Modal';
import { acceptSuggestion, addSuggestion, getSuggestionBase, getSuggestions, rejectSuggestion } from '../../services/CollabService';
import { patchLines, suggestionLines, suggestionLinesLabel, suggestionsLabel } from '../../utils/collab3Model';
import { ago } from '../../utils/blameModel';
import { ParticipantMark } from '../presence/Presence';
import './Collab.css';

const LANGUAGES = { js: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript', json: 'json', css: 'css', html: 'html', md: 'markdown', py: 'python', java: 'java', go: 'go', yml: 'yaml', yaml: 'yaml' };
const languageOf = (path) => LANGUAGES[String(path || '').split('.').pop().toLowerCase()] || 'plaintext';

export function SuggestionEditor({ projectId, path, isOpen, onClose, onSubmitted = null }) {
  const [base, setBase] = useState(null);
  const [content, setContent] = useState('');
  const [message, setMessage] = useState('');
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !projectId || !path) { return undefined; }
    let cancelled = false;
    setState('loading'); setError(null); setMessage('');
    getSuggestionBase(projectId, path).then((answer) => {
      if (cancelled) { return; }
      setBase(answer);
      setContent(answer?.content || '');
      setState('editing');
    }).catch((e) => {
      if (!cancelled) { setError(e.message || 'The file could not be loaded.'); setState('error'); }
    });
    return () => { cancelled = true; };
  }, [isOpen, projectId, path]);

  const submit = async () => {
    if (!message.trim()) { setError('Say in a few words what you suggest and why.'); return; }
    if (content === (base?.content || '')) { setError('Nothing changed yet: edit the file above first.'); return; }
    setState('sending'); setError(null);
    try {
      const answer = await addSuggestion(projectId, { path, baseSha: base?.sha || null, content, message: message.trim() });
      setState('sent');
      if (onSubmitted) { onSubmitted(answer?.suggestion); }
      setTimeout(onClose, 900);
    } catch (e) {
      setError(e.message || 'The suggestion could not be sent.');
      setState('editing');
    }
  };

  if (!isOpen) { return null; }
  return (
    <Modal className="collab-suggest-modal" isOpen onClose={state === 'sending' ? null : onClose} title={`Suggest a change to ${String(path).split('/').pop()}`} type="custom">
      <div className="collab-suggest" data-testid="suggestion-editor">
        <p className="collab-muted collab-small">
          You are a viewer: your edits stay a suggestion, here, until an editor accepts them. The live file is not changed.
        </p>
        <div className="collab-suggest-editor">
          {state === 'loading' && <div className="collab-muted collab-pad">Loading the file…</div>}
          {state !== 'loading' && state !== 'error' && (
            <Editor
              height="100%"
              language={languageOf(path)}
              onChange={(value) => setContent(value ?? '')}
              options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, readOnly: state === 'sending' || state === 'sent' }}
              path={`suggestion://${projectId}/${path}`}
              value={content}
            />
          )}
        </div>
        <input
          aria-label="What you suggest and why"
          className="collab-input"
          data-testid="suggestion-message"
          maxLength={500}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What you suggest and why (shown to the editors)"
          value={message}
        />
        {error && <div className="collab-error collab-small">{error}</div>}
        {state === 'sent' && <div className="collab-small" data-testid="suggestion-sent">Sent. You get a notification when an editor accepts or declines it.</div>}
      </div>
      <div className="modal-footer">
        <button className="modal-btn modal-btn-cancel" disabled={state === 'sending'} onClick={onClose} type="button">Cancel</button>
        <button className="modal-btn modal-btn-confirm" data-testid="suggestion-submit" disabled={state !== 'editing'} onClick={submit} type="button">Send suggestion</button>
      </div>
    </Modal>
  );
}

SuggestionEditor.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func,
  path: PropTypes.string,
  projectId: PropTypes.string,
};

function PatchView({ patch }) {
  const lines = useMemo(() => patchLines(patch), [patch]);
  if (lines.length === 0) { return <div className="collab-muted collab-pad">No text changes.</div>; }
  return (
    <div className="collab-diff" data-testid="suggestion-diff">
      {lines.slice(0, 400).map((line, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className={`collab-diff-line is-${line.kind}`} key={index}>
          <span className="collab-diff-n">{line.kind === 'del' ? line.oldLine : line.kind === 'hunk' ? '' : line.newLine}</span>
          <span className="collab-diff-m">{line.kind === 'add' ? '+' : line.kind === 'del' ? '−' : ''}</span>
          <span className="collab-diff-t">{line.text}</span>
        </div>
      ))}
    </div>
  );
}

PatchView.propTypes = { patch: PropTypes.string };

/** What a suggestion is, in one sentence (the Suggestions dialog and the editor's Suggestions tab say the same). */
export const SUGGESTION_EXPLAINER = 'A suggestion is a change to a file that someone who can only view the project proposes with Suggest a change in Code.';
/** Where a suggestion comes from, on every row. */
export const SUGGESTION_ORIGIN = 'made with Suggest a change in Code';

const STATUS = { pending: 'Waiting for an editor', accepted: 'Accepted', rejected: 'Declined' };

// ---- Between the lists and the open editor (window events) ----

let pendingOpen = null; // { projectId, path, id, at }: an Open in Code the editor of that file has not shown yet
const PENDING_MS = 30000;

/**
 * Open in Code: the file at the suggestion's first line (UnifiedTopBar listens for `flow:open-code`, as the comments'
 * file links do), then the editor's side panel on its Suggestions tab, on this suggestion (`collab:openSuggestion`; an
 * editor that opens the file only later picks the request up when it mounts).
 */
export function openSuggestionInCode(projectId, suggestion) {
  if (!suggestion?.path || typeof window === 'undefined') { return; }
  const range = suggestionLines(suggestion.patch);
  pendingOpen = { projectId, path: suggestion.path, id: suggestion.id, at: Date.now() };
  window.dispatchEvent(new CustomEvent('flow:open-code', { detail: { path: suggestion.path, line: range?.start || null } }));
  window.dispatchEvent(new CustomEvent('collab:openSuggestion', { detail: { projectId, path: suggestion.path, suggestionId: suggestion.id } }));
}

/** The editor of `path` shows a suggestion on its Suggestions tab when asked (`onOpen(suggestionId)`). */
export function useSuggestionRequests({ projectId, path, onOpen }) {
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  useEffect(() => {
    if (!projectId || !path) { return undefined; }
    const mine = (request) => request && (!request.projectId || request.projectId === projectId) && request.path === path;
    if (mine(pendingOpen) && Date.now() - pendingOpen.at < PENDING_MS) {
      const { id } = pendingOpen;
      pendingOpen = null;
      onOpenRef.current(id);
    }
    const onEvent = (event) => {
      const detail = event?.detail;
      if (!mine(detail)) { return; }
      pendingOpen = null;
      onOpenRef.current(detail.suggestionId || null);
    };
    window.addEventListener('collab:openSuggestion', onEvent);
    return () => window.removeEventListener('collab:openSuggestion', onEvent);
  }, [projectId, path]);
}

/**
 * "Suggest a change" from outside the editor (the Suggestions dialog, for someone who can only view): the open file's
 * Suggest a change when a viewer's editor is up (it answers `handled`), else Code, to pick a file (`flow:open-code`).
 */
export function requestSuggestChange(projectId) {
  if (typeof window === 'undefined') { return; }
  const event = new CustomEvent('collab:suggest', { detail: { projectId, handled: false } });
  window.dispatchEvent(event);
  if (!event.detail.handled) { window.dispatchEvent(new CustomEvent('flow:open-code', { detail: { path: null } })); }
}

/** A viewer's editor answers `collab:suggest` with its own Suggest a change (`onSuggest()`). */
export function useSuggestChangeRequests({ projectId, enabled, onSuggest }) {
  const onSuggestRef = useRef(onSuggest);
  onSuggestRef.current = onSuggest;
  useEffect(() => {
    if (!enabled || !projectId) { return undefined; }
    const onEvent = (event) => {
      const detail = event?.detail;
      if (!detail || detail.handled || (detail.projectId && detail.projectId !== projectId)) { return; }
      detail.handled = true;
      onSuggestRef.current();
    };
    window.addEventListener('collab:suggest', onEvent);
    return () => window.removeEventListener('collab:suggest', onEvent);
  }, [projectId, enabled]);
}

// ---- One suggestion ----

/**
 * A suggestion: who, which file and lines, when, made with Suggest a change in Code, its status; Accept / Decline for
 * editors and the owner. In the dialog: Open in Code and the diff. `compact` (the editor's Suggestions tab, one file):
 * the lines instead of the path, Go to line, the diff behind Show the change.
 */
export function SuggestionCard({ suggestion, canDecide, onDecided, projectId, focused = false, compact = false, onOpenInCode = null, onGoToLine = null }) {
  const [phase, setPhase] = useState('idle');
  const [reason, setReason] = useState('');
  const [conflicts, setConflicts] = useState(null);
  const [error, setError] = useState(null);
  const [diffOpen, setDiffOpen] = useState(!compact || focused);
  const ref = useRef(null);
  const pending = suggestion.status === 'pending';
  const range = useMemo(() => suggestionLines(suggestion.patch), [suggestion.patch]);
  const lines = suggestionLinesLabel(range);
  useEffect(() => {
    if (!focused) { return; }
    setDiffOpen(true);
    if (ref.current?.scrollIntoView) { ref.current.scrollIntoView({ block: 'nearest' }); }
  }, [focused]);

  const accept = async () => {
    setPhase('busy'); setError(null); setConflicts(null);
    try {
      await acceptSuggestion(projectId, suggestion.id);
      onDecided();
    } catch (e) {
      if (e.status === 409) { setConflicts(e.body?.conflicts || []); } else { setError(e.message); }
    } finally {
      setPhase('idle');
    }
  };
  const reject = async () => {
    setPhase('busy'); setError(null);
    try {
      await rejectSuggestion(projectId, suggestion.id, reason.trim());
      onDecided();
    } catch (e) {
      setError(e.message);
      setPhase('rejecting');
    }
  };

  const who = <strong>{suggestion.by?.name || 'Someone'}</strong>;
  return (
    <section className={`collab-group${focused ? ' is-focused' : ''}${compact ? ' is-compact' : ''}`} data-testid="suggestion-card" ref={ref}>
      <div className="collab-group-head">
        <ParticipantMark initials={suggestion.by?.initials || '?'} label={suggestion.by?.name} size={compact ? 20 : 24} slot={suggestion.by?.slot ?? 0} />
        <div className="collab-group-title">
          <div data-testid="suggestion-where">
            {compact
              ? <>{who} suggests a change{lines ? ` to ${lines}` : ''}</>
              : <>{who} suggests a change to <span className="collab-mono">{suggestion.path}</span>{lines ? `, ${lines}` : ''}</>}
          </div>
          <div className="collab-muted collab-small" data-testid="suggestion-origin">
            {ago(suggestion.at)} · {SUGGESTION_ORIGIN} · <span className="collab-add">+{suggestion.added || 0}</span> <span className="collab-del">−{suggestion.removed || 0}</span>
          </div>
          <div className="collab-small">
            <span className={`collab-suggestion-status is-${suggestion.status}`} data-testid="suggestion-status">{STATUS[suggestion.status] || suggestion.status}</span>
            <span className="collab-muted">{suggestion.decidedBy ? ` by ${suggestion.decidedBy.name}` : ''}{suggestion.reason ? `: “${suggestion.reason}”` : ''}</span>
          </div>
        </div>
        <div className="collab-suggestion-actions">
          {onOpenInCode && <button className="collab-btn" data-testid="suggestion-open-code" onClick={() => onOpenInCode(suggestion)} title={`Open ${String(suggestion.path).split('/').pop()}${lines ? ` at ${lines}` : ''} in Code, with this suggestion next to the comments`} type="button">Open in Code</button>}
          {pending && canDecide && phase !== 'rejecting' && (
            <>
              <button className="collab-btn is-primary" data-testid="suggestion-accept" disabled={phase === 'busy'} onClick={accept} type="button">Accept</button>
              <button className="collab-btn" data-testid="suggestion-reject" disabled={phase === 'busy'} onClick={() => setPhase('rejecting')} type="button">Decline…</button>
            </>
          )}
        </div>
      </div>
      {suggestion.message && <div className="collab-pad">“{suggestion.message}”</div>}
      {phase === 'rejecting' && (
        <div className="collab-pad collab-reject">
          <input className="collab-input" data-testid="suggestion-reject-reason" maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="Why (the viewer sees it)" value={reason} />
          <button className="collab-btn" data-testid="suggestion-reject-confirm" onClick={reject} type="button">Decline</button>
          <button className="collab-link" onClick={() => setPhase('idle')} type="button">Cancel</button>
        </div>
      )}
      {conflicts && (
        <div className="collab-pad" data-testid="suggestion-conflicts">
          <strong>Not applied.</strong> The file changed since the suggestion was made, on the same lines:
          <ul className="collab-conflicts">
            {conflicts.map((c, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index}><span className="collab-mono">{c.path || suggestion.path}</span>{c.lines ? ` lines ${c.lines.start}${c.lines.end !== c.lines.start ? `–${c.lines.end}` : ''}` : ''}{c.by?.name ? ` · changed by ${c.by.name}` : ''}</li>
            ))}
          </ul>
          Ask {suggestion.by?.name || 'them'} to suggest it again on the current file, or make the change by hand.
        </div>
      )}
      {error && <div className="collab-error collab-pad collab-small">{error}</div>}
      {compact && (
        <div className="collab-pad collab-suggestion-links">
          {onGoToLine && range && <button className="collab-link collab-small" data-testid="suggestion-go-to-line" onClick={() => onGoToLine(range.start)} type="button">Go to {lines}</button>}
          <button aria-expanded={diffOpen} className="collab-link collab-small" data-testid="suggestion-toggle-diff" onClick={() => setDiffOpen(!diffOpen)} type="button">{diffOpen ? 'Hide the change' : 'Show the change'}</button>
        </div>
      )}
      {diffOpen && <PatchView patch={suggestion.patch} />}
    </section>
  );
}

SuggestionCard.propTypes = {
  canDecide: PropTypes.bool,
  compact: PropTypes.bool,
  focused: PropTypes.bool,
  onDecided: PropTypes.func.isRequired,
  onGoToLine: PropTypes.func,
  onOpenInCode: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  suggestion: PropTypes.object.isRequired,
};

// ---- The list, and the dialog ----

/**
 * The project's suggestions, Waiting / All, each a SuggestionCard (the Suggestions dialog, Team ▾ › Notes ›
 * Suggestions). `onClose` closes what holds the list before Open in Code or Suggest a change; `onWaiting(n)`: how many
 * wait for this person to decide (0 for a viewer), each time the list loads.
 */
export function SuggestionsList({ projectId, onClose, focusId = null, onWaiting = null }) {
  const [status, setStatus] = useState('pending');
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const onWaitingRef = useRef(onWaiting);
  onWaitingRef.current = onWaiting;

  const load = useCallback(async () => {
    if (!projectId) { return; }
    try {
      setAnswer(await getSuggestions(projectId, { status }));
      setError(null);
    } catch (e) {
      setError(e.message || 'Suggestions could not be loaded.');
    }
  }, [projectId, status]);

  useEffect(() => { load(); }, [load]);
  const list = answer?.suggestions || [];
  const canDecide = Boolean(answer?.canDecide);
  const waiting = status === 'pending' ? list.length : list.filter((s) => s.status === 'pending').length;
  useEffect(() => { if (answer) { onWaitingRef.current?.(canDecide ? waiting : 0); } }, [answer, canDecide, waiting]);
  const openInCode = (suggestion) => { onClose(); openSuggestionInCode(projectId, suggestion); };
  const suggest = () => { onClose(); requestSuggestChange(projectId); };
  return (
    <div className="collab-suggestions" data-testid="suggestions-panel">
      <div className="collab-messages-bar">
        <div aria-label="Show" className="collab-seg" role="group">
          {[['pending', 'Waiting'], ['all', 'All']].map(([value, label]) => (
            <button aria-pressed={status === value} className={status === value ? 'is-on' : ''} key={value} onClick={() => setStatus(value)} type="button">{label}</button>
          ))}
        </div>
        {answer && !canDecide && list.length > 0 && <button className="collab-btn is-primary" data-testid="suggestions-suggest" onClick={suggest} type="button">Suggest a change</button>}
      </div>
      {error && <div className="collab-error">{error}</div>}
      {!answer && !error && <div className="collab-muted">Loading…</div>}
      {answer && list.length === 0 && (
        <div className="collab-messages-empty" data-testid="suggestions-empty">
          <strong>No suggestions {status === 'pending' ? 'waiting' : 'yet'}.</strong>
          <span data-testid="suggestions-howto">
            {SUGGESTION_EXPLAINER}{canDecide ? ' Suggestions from viewers show here for you to accept or decline.' : ''}
          </span>
          {!canDecide && <button className="collab-btn is-primary collab-empty-action" data-testid="suggestions-suggest" onClick={suggest} type="button">Suggest a change</button>}
        </div>
      )}
      {answer && !canDecide && list.length > 0 && (
        <p className="collab-messages-note" data-testid="suggestions-viewer">You can view this project: editors and the owner accept or decline suggestions.</p>
      )}
      {list.map((s) => (
        <SuggestionCard canDecide={canDecide} focused={s.id === focusId} key={s.id} onDecided={load} onOpenInCode={openInCode} projectId={projectId} suggestion={s} />
      ))}
    </div>
  );
}

SuggestionsList.propTypes = {
  focusId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onWaiting: PropTypes.func,
  projectId: PropTypes.string,
};

export function SuggestionsPanel({ projectId, isOpen, onClose, focusId = null }) {
  const [waiting, setWaiting] = useState(0);
  if (!isOpen) { return null; }
  return (
    <Modal className="collab-changes-modal" isOpen onClose={onClose} title={suggestionsLabel(waiting)} type="custom">
      <SuggestionsList focusId={focusId} onClose={onClose} onWaiting={setWaiting} projectId={projectId} />
    </Modal>
  );
}

SuggestionsPanel.propTypes = {
  focusId: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
};

// ---- The editor's Suggestions tab ----

/** One file's suggestions, every status: `{ answer, reload }` (`answer` null while loading, `{ suggestions, canDecide }`). */
export function useFileSuggestions(projectId, path, reloadKey = 0) {
  const [answer, setAnswer] = useState(null);
  const load = useCallback(async () => {
    if (!projectId || !path) { setAnswer(null); return; }
    try {
      setAnswer(await getSuggestions(projectId, { status: 'all', path }));
    } catch {
      setAnswer({ suggestions: [], canDecide: false, failed: true });
    }
  }, [projectId, path]);
  useEffect(() => { load(); }, [load, reloadKey]);
  return { answer, reload: load };
}

/** The open file's suggestions, waiting ones first, in the editor's side panel. */
export function FileSuggestions({ projectId, path, answer, onChanged, focusId = null, onGoToLine = null, onSuggest = null }) {
  if (!answer) { return <div className="collab-muted collab-small">Loading…</div>; }
  const list = [...(answer.suggestions || [])].sort((a, b) => (a.status === 'pending' ? 0 : 1) - (b.status === 'pending' ? 0 : 1));
  const canDecide = Boolean(answer.canDecide);
  const name = String(path || '').split('/').pop();
  return (
    <div className="collab-file-suggestions" data-testid="file-suggestions">
      {answer.failed && <div className="collab-error collab-small">Suggestions could not be loaded.</div>}
      {list.length === 0 && !answer.failed && (
        <div className="collab-messages-empty" data-testid="file-suggestions-empty">
          <strong>No suggestions on {name}.</strong>
          <span>{SUGGESTION_EXPLAINER}{canDecide ? ' Suggestions from viewers show here for you to accept or decline.' : ''}</span>
          {!canDecide && onSuggest && <button className="collab-btn is-primary collab-empty-action" data-testid="file-suggestions-suggest" onClick={onSuggest} type="button">Suggest a change</button>}
        </div>
      )}
      {list.length > 0 && !canDecide && onSuggest && (
        <button className="collab-btn" data-testid="file-suggestions-suggest" onClick={onSuggest} style={{ alignSelf: 'flex-start' }} type="button">Suggest a change</button>
      )}
      {list.map((s) => (
        <SuggestionCard canDecide={canDecide} compact focused={s.id === focusId} key={s.id} onDecided={onChanged} onGoToLine={onGoToLine} projectId={projectId} suggestion={s} />
      ))}
    </div>
  );
}

FileSuggestions.propTypes = {
  answer: PropTypes.object,
  focusId: PropTypes.string,
  onChanged: PropTypes.func.isRequired,
  onGoToLine: PropTypes.func,
  onSuggest: PropTypes.func,
  path: PropTypes.string,
  projectId: PropTypes.string.isRequired,
};
